import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { ArrowBack, PictureAsPdf } from "@material-ui/icons";
import { connect } from "react-redux";
import HeaderDescription from "../../../components/commons/HeaderDescription";
import MOButtonV2 from "../../../components/commons/MOButtonV2";
import DeleteModal from "../../../components/commons/Modals/DeleteModal";
import mobioApi from "../../../services";
import { toastMessage } from "../../../helpers/toastMessage";
import {
  getSourceTypeLabel,
  getStatusClassName,
  getStatusLabel,
} from "./knowledgeDocuments";
import {
  formatDocumentDate,
  formatFileSize,
  getDocumentFile,
} from "./knowledgeDocumentUtils";

const KnowledgeDocumentView = ({ id, token }) => {
  const navigate = useNavigate();
  const [knowledgeDocument, setKnowledgeDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const getDocument = async () => {
      try {
        setLoading(true);
        const api = mobioApi(token);
        const response = await api.getDocument(id);
        setKnowledgeDocument(response?.data?.data || null);
      } catch (error) {
        setKnowledgeDocument(null);
        toastMessage(
          "error",
          error?.response?.data?.msg || "This document could not be loaded."
        );
      } finally {
        setLoading(false);
      }
    };

    getDocument();
  }, [id, token]);

  const handleDeleteDocument = async () => {
    if (!knowledgeDocument?.id || deleting) {
      return;
    }

    try {
      setDeleting(true);
      const api = mobioApi(token);
      const response = await api.deleteDocument(knowledgeDocument.id);
      toastMessage("success", response?.data?.msg || "Document deleted.");
      setDeleteModalOpen(false);
      navigate("/dashboard/ai-area/upload-documents");
    } catch (error) {
      toastMessage(
        "error",
        error?.response?.data?.msg || "This document could not be deleted."
      );
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="ai-area-page">
        <button
          type="button"
          className="ai-area-back-btn"
          onClick={() => navigate("/dashboard/ai-area/upload-documents")}
        >
          <ArrowBack className="ai-area-back-icon" />
          Back to documents
        </button>
        <HeaderDescription
          heading="Upload Documents"
          description="Loading document..."
        />
      </div>
    );
  }

  if (!knowledgeDocument) {
    return (
      <div className="ai-area-page">
        <button
          type="button"
          className="ai-area-back-btn"
          onClick={() => navigate("/dashboard/ai-area/upload-documents")}
        >
          <ArrowBack className="ai-area-back-icon" />
          Back to documents
        </button>
        <HeaderDescription
          heading="Document not found"
          description="This document is no longer available. Go back to the list to open another one."
        />
      </div>
    );
  }

  const documentFile = getDocumentFile(knowledgeDocument);

  return (
    <div className="ai-area-page">
      <button
        type="button"
        className="ai-area-back-btn"
        onClick={() => navigate("/dashboard/ai-area/upload-documents")}
      >
        <ArrowBack className="ai-area-back-icon" />
        Back to documents
      </button>

      <div className="ai-area-page-header">
        <HeaderDescription
          heading={knowledgeDocument.title}
          description="Review this document. You can update it if the information needs to change."
        />
        <div className="ai-area-page-header-actions">
          <MOButtonV2
            text="Delete"
            textColor="#E53E3E"
            radius={4}
            backgroundColor="#ffffff"
            border="1px solid #E53E3E"
            height={36}
            fontSize={13}
            padding="8px 20px"
            onClick={() => setDeleteModalOpen(true)}
          />
          <MOButtonV2
            text="Edit"
            textColor="#ffffff"
            radius={4}
            backgroundColor="#29AEE6"
            height={36}
            fontSize={13}
            padding="8px 20px"
            onClick={() =>
              navigate(
                `/dashboard/ai-area/upload-documents/edit/${knowledgeDocument.id}`
              )
            }
          />
        </div>
      </div>

      <div className="ai-area-document-panel">
        <div className="ai-area-document-meta">
          <div className="ai-area-document-meta-item">
            <span className="ai-area-document-meta-label">Type</span>
            <span className="ai-area-document-meta-value">
              {getSourceTypeLabel(knowledgeDocument.source_type)}
            </span>
          </div>
          <div className="ai-area-document-meta-item">
            <span className="ai-area-document-meta-label">Status</span>
            <span
              className={`ai-area-status-badge ai-area-status-badge-${getStatusClassName(
                knowledgeDocument.status
              )}`}
            >
              {getStatusLabel(knowledgeDocument.status)}
            </span>
          </div>
          <div className="ai-area-document-meta-item">
            <span className="ai-area-document-meta-label">Version</span>
            <span className="ai-area-document-meta-value">
              {knowledgeDocument.version}
            </span>
          </div>
          <div className="ai-area-document-meta-item">
            <span className="ai-area-document-meta-label">Created</span>
            <span className="ai-area-document-meta-value">
              {formatDocumentDate(knowledgeDocument.created_at)}
            </span>
          </div>
          <div className="ai-area-document-meta-item">
            <span className="ai-area-document-meta-label">Updated</span>
            <span className="ai-area-document-meta-value">
              {formatDocumentDate(knowledgeDocument.updated_at)}
            </span>
          </div>
        </div>

        <div className="ai-area-document-content">
          <h3 className="ai-area-document-content-title">Document file</h3>
          {documentFile.fileName ? (
            <div className="ai-area-pdf-file ai-area-pdf-file-view">
              <div className="ai-area-pdf-file-info">
                <PictureAsPdf className="ai-area-pdf-icon" />
                <div>
                  <p className="ai-area-pdf-file-name">{documentFile.fileName}</p>
                  <p className="ai-area-pdf-file-meta">
                    PDF
                    {documentFile.fileSize
                      ? ` · ${formatFileSize(documentFile.fileSize)}`
                      : ""}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <p className="ai-area-document-content-text">
              No PDF has been added to this document yet.
            </p>
          )}
        </div>

        <div className="ai-area-document-content">
          <h3 className="ai-area-document-content-title">Document text</h3>
          {knowledgeDocument.content ? (
            <div className="ai-area-document-content-box">
              <p className="ai-area-document-content-text">
                {knowledgeDocument.content}
              </p>
            </div>
          ) : (
            <p className="ai-area-document-content-text">
              No text is available for this document yet.
            </p>
          )}
        </div>
      </div>

      <DeleteModal
        title="Delete Document"
        heading="Are You Sure?"
        description="This document will be permanently deleted. The AI will no longer be able to use it."
        modalOpen={deleteModalOpen}
        setModalOpen={setDeleteModalOpen}
        apiCall={handleDeleteDocument}
      />
    </div>
  );
};

const mapState = (state) => ({
  token: state.userInfo.token,
});

export default connect(mapState)(KnowledgeDocumentView);
